import {
  MESSAGE_TYPE,
  CONVERSATION_PRIORITY_ORDER,
} from 'shared/constants/messages';
import { toEpochSeconds } from 'dashboard/mixins/conversations';
import { applyPageFilters } from './helpers';
import {
  CONVERSATION_LIST_KEYS,
  findConversationByDisplayId,
  getSelectedChatConversation,
} from './helpers/conversationLists';

const sortComparator = {
  latest: (a, b) => b.last_activity_at - a.last_activity_at,
  sort_on_created_at: (a, b) => a.created_at - b.created_at,
  sort_on_priority: (a, b) => {
    const priorityRank = priority =>
      CONVERSATION_PRIORITY_ORDER[priority ?? null] ??
      CONVERSATION_PRIORITY_ORDER.null;
    const diff = priorityRank(a.priority) - priorityRank(b.priority);
    if (diff !== 0) return diff;
    return b.last_activity_at - a.last_activity_at;
  },
  sort_on_waiting_since: (a, b) => {
    if (!a.waiting_since && !b.waiting_since) {
      return a.created_at - b.created_at;
    }

    if (!a.waiting_since) {
      return 1;
    }

    if (!b.waiting_since) {
      return -1;
    }

    return a.waiting_since - b.waiting_since;
  },
};

function sortConversationList(list, sortFilter) {
  const comparator = sortComparator[sortFilter] || sortComparator.latest;
  return [...list].sort(comparator);
}

function allLoadedConversationsUnique(state) {
  const seen = new Set();
  const out = [];
  CONVERSATION_LIST_KEYS.forEach(key => {
    (state.conversationLists[key] || []).forEach(c => {
      const n = Number(c.id);
      if (!seen.has(n)) {
        seen.add(n);
        out.push(c);
      }
    });
  });
  return out;
}

function getExcludedFromDefaultViewsLabelTitles(rootGetters) {
  return (rootGetters?.['labels/getLabels'] || [])
    .filter(label => label.exclude_from_default_views)
    .map(label => label.title);
}

// getters
const getters = {
  getAllConversations: state => {
    return sortConversationList(
      allLoadedConversationsUnique(state),
      state.chatSortFilter
    );
  },
  getAppliedFilterConversations: state => {
    return sortConversationList(
      state.conversationLists.appliedFilters || [],
      state.chatSortFilter
    );
  },
  getSelectedChat: state => {
    const { selectedChatId } = state;
    const selectedChat = findConversationByDisplayId(state, selectedChatId);
    return selectedChat || {};
  },
  getVisibleConversationIds: state => state.visibleConversationIds || [],
  getDetachedListHighlight: state =>
    state.detachedListHighlight || {
      active: false,
      alternateHighlight: false,
      anchorConversationId: null,
      routePath: '',
    },
  getSelectedChatAttachments: (_state, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    return selectedChat.attachments || [];
  },
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const reversed = [...messages].reverse();

    const hasEmailHeaders = message => {
      const contentAttributes = message.content_attributes || {};
      const email = contentAttributes.email || {};
      const hasFrom = email.from && email.from.length;
      const hasTo =
        (email.to && email.to.length) ||
        (contentAttributes.to_emails && contentAttributes.to_emails.length);
      const hasCc =
        (email.cc && email.cc.length) ||
        (contentAttributes.cc_emails && contentAttributes.cc_emails.length);
      return hasFrom || hasTo || hasCc;
    };

    // Always the chronologically last email — same message ⋮ Reply would use
    return reversed.find(
      message =>
        (message.message_type === MESSAGE_TYPE.OUTGOING ||
          message.message_type === MESSAGE_TYPE.INCOMING) &&
        hasEmailHeaders(message)
    );
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;
    const excludedLabels = getExcludedFromDefaultViewsLabelTitles(rootGetters);
    const filtered = _state.conversationLists.me.filter(conversation => {
      const { assignee } = conversation.meta;
      const isAssignedToMe = assignee && assignee.id === currentUserID;
      const shouldFilter = applyPageFilters(
        conversation,
        activeFilters,
        excludedLabels
      );
      return isAssignedToMe && shouldFilter;
    });
    return sortConversationList(filtered, _state.chatSortFilter);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getUnAssignedChats: (_state, _, __, rootGetters) => activeFilters => {
    const excludedLabels = getExcludedFromDefaultViewsLabelTitles(rootGetters);
    const filtered = _state.conversationLists.unassigned.filter(
      conversation => {
        const isUnAssigned = !conversation.meta.assignee;
        const shouldFilter = applyPageFilters(
          conversation,
          activeFilters,
          excludedLabels
        );
        return isUnAssigned && shouldFilter;
      }
    );
    return sortConversationList(filtered, _state.chatSortFilter);
  },
  getAllStatusChats: (_state, _, __, rootGetters) => activeFilters => {
    const excludedLabels = getExcludedFromDefaultViewsLabelTitles(rootGetters);
    const filtered = _state.conversationLists.all.filter(conversation => {
      const shouldFilter = applyPageFilters(
        conversation,
        activeFilters,
        excludedLabels
      );
      return shouldFilter;
    });
    return sortConversationList(filtered, _state.chatSortFilter);
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    const msgs = chat.messages || [];
    const seenSec = toEpochSeconds(chat.agent_last_seen_at);
    return msgs.filter(
      chatMessage =>
        toEpochSeconds(chatMessage.created_at) > seenSec &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getContactSidebarUiFlags: ({ contactSidebarUiFlags }) => contactSidebarUiFlags,
  getConversationsForContact: state => contactId => {
    if (
      contactId === undefined ||
      contactId === null ||
      contactId === '' ||
      Number.isNaN(Number(contactId))
    ) {
      return [];
    }
    const n = Number(contactId);
    return allLoadedConversationsUnique(state)
      .filter(
        c =>
          Number(c.meta?.sender?.id) === n || Number(c.contact_id) === n
      )
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  },
  getConversationById: _state => conversationId => {
    return findConversationByDisplayId(_state, conversationId);
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },
};

export { getSelectedChatConversation };
export default getters;
